import {
  Box,
  BoxProps,
  Button,
  Flex,
  HStack,
  IconButton,
  IconButtonProps,
  IconProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuProps,
  Portal,
  Text,
  UseDisclosureReturn,
  VStack,
  useDisclosure,
  useMenuContext
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import RevealOnGroupHover from "@pathwright/ux/src/components/style/RevealOnGroupHover"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import classNames from "classnames"
import { motion } from "framer-motion"
import { MouseEvent } from "react"

export type OptionsMenuItemProps = {
  icon?: IconName
  iconProps?: IconProps
  label: string
  closeOnClick?: boolean
} & LinkProps

export const OptionsMenuItem = ({
  icon,
  iconProps,
  label,
  onClick,
  closeOnClick,
  ...props
}: OptionsMenuItemProps) => {
  const menu = useMenuContext()

  return (
    <Link
      as={MenuItem}
      onClick={(e) => {
        onClick?.(e as MouseEvent<HTMLAnchorElement>)
        if (closeOnClick) {
          menu?.onClose()
        }
      }}
      w="100%"
      display="block"
      borderRadius="none"
      py={1.5}
      px={2}
      color="gray.500"
      aria-label={label}
      underlineBehavior="never"
      _hover={{ bg: "blackAlpha.200" }}
      {...props}
    >
      <HStack align="center" justify="flex-start" w="100%">
        {!!icon && <Pathicon icon={icon} {...iconProps} />}
        <Text fontSize="sm" my={0} fontWeight="normal">
          {label}
        </Text>
      </HStack>
    </Link>
  )
}

export type OptionsMenuProps = {
  children?:
    | React.ReactNode
    | ((props: UseDisclosureReturn) => React.ReactNode | JSX.Element)
  options?: OptionsMenuItemProps[]
  selectedOptionLabel?: string
  // Conditionally render PopoverContent in a portal.
  renderPortal?: boolean
  triggerProps?: IconButtonProps
  menuProps?: Partial<MenuProps>
  renderOnGroupHover?: boolean
} & Omit<BoxProps, "children">

const OptionsMenu = ({
  children,
  options,
  selectedOptionLabel,
  renderPortal,
  triggerProps,
  menuProps,
  className,
  renderOnGroupHover,
  ...props
}: OptionsMenuProps) => {
  const disclosure = useDisclosure()

  // Store MenuList for conditionally rendering in portal.
  const content = disclosure.isOpen ? (
    <MenuList
      w="fit-content"
      zIndex="modal"
      border="solid 1px"
      borderColor="gray.100"
      borderRadius="4px"
      outline="none"
      boxShadow="none"
      overflow="hidden"
      p={0}
      minW={0}
    >
      <VStack align="flex-start" gap={0}>
        {typeof children === "function"
          ? children(disclosure)
          : children || null}
        {options?.map((item, index) => (
          <OptionsMenuItem
            key={index}
            icon={item.icon}
            label={item.label}
            onClick={item.onClick}
          />
        ))}
      </VStack>
    </MenuList>
  ) : null

  // Conditionally revealing on group hover.
  const TriggerRenderingComponent = renderOnGroupHover
    ? RevealOnGroupHover
    : Box

  return (
    <Flex
      as={motion.div}
      className={classNames("OptionMenu", className)}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: { ease: "easeInOut", duration: 0.3, delay: 0.1 }
      }}
      {...props}
    >
      <Menu
        placement="right-start"
        // Defer rendering MenuContent until opened.
        // There seems to be a glitch with Menu that causes
        // it to render in the wrong place before it's been initially
        // opened, which can cause, in this case, the Menu to
        // add white space to right of UI.
        isLazy
        {...disclosure}
        {...menuProps}
      >
        <TriggerRenderingComponent
          // Ensure button is always visible when disclosure is open.
          opacity={disclosure.isOpen ? 1 : undefined}
        >
          {selectedOptionLabel ? (
            <MenuButton
              as={Button}
              rightIcon={<Pathicon icon="chevron-down" />}
              size="sm"
              variant="ghost"
              {...triggerProps}
            >
              {selectedOptionLabel}
            </MenuButton>
          ) : (
            <MenuButton
              as={IconButton}
              icon={<Pathicon icon="ellipses-horizontal" />}
              aria-label="Options Menu"
              size="xs"
              {...triggerProps}
            />
          )}
        </TriggerRenderingComponent>
        {renderPortal ? <Portal>{content}</Portal> : content}
      </Menu>
    </Flex>
  )
}

export default OptionsMenu
